import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { Sidebar } from './components/Sidebar';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { SubscriptionProvider, useSubscription } from './lib/subscription';
import { setUpgradeHandler } from './lib/api';
import { UpgradeModal } from './components/UpgradePrompt';
import api from './lib/api';

import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import FeaturesPage from './pages/FeaturesPage';
import HowItWorksPage from './pages/HowItWorksPage';
import PricingPage from './pages/PricingPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import ResumePage from './pages/ResumePage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import InterviewPrepPage from './pages/InterviewPrepPage';
import CommunityPage from './pages/CommunityPage';
import GamificationPage from './pages/GamificationPage';
import SubscriptionPage from './pages/SubscriptionPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import KaireraAssessmentPage from './pages/KaireraAssessmentPage';
import KaireraDashboardPage from './pages/KaireraDashboardPage';
import KaireraAssessmentBatteriesPage from './pages/KaireraAssessmentBatteriesPage';
import KaireraReportPage from './pages/KaireraReportPage';
import KaireraToolsPage from './pages/KaireraToolsPage';
import GapAnalysisPage from './pages/GapAnalysisPage';
import ComingSoonPage from './pages/ComingSoonPage';
import ReferralPage from './pages/ReferralPage';
import AnalyticsPage from './pages/AnalyticsPage';
import LinkedInOptimizerPage from './pages/LinkedInOptimizerPage';
import CompanyIntelPage from './pages/CompanyIntelPage';
import CareerSimulatorPage from './pages/CareerSimulatorPage';
import PublicSharedAssessmentPage from './pages/PublicSharedAssessmentPage';
import PublicSharedResumePage from './pages/PublicSharedResumePage';
import '@/App.css';

function ProtectedLayout({ user, onLogout, children }) {
  return (
    <div className="flex min-h-screen" style={{ background: 'var(--t-bg)' }}>
      <Sidebar user={user} onLogout={onLogout} />
      <main className="flex-1 overflow-y-auto w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pt-14 lg:pt-8">
          {children}
        </div>
      </main>
    </div>
  );
}

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardingComplete, setOnboardingComplete] = useState(null);
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const { theme } = useTheme();
  const { refresh: refreshSub } = useSubscription();

  // Register global upgrade handler
  useEffect(() => {
    setUpgradeHandler((detail) => {
      setUpgradeInfo(detail);
    });
    return () => setUpgradeHandler(null);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  // Check onboarding status when user logs in
  useEffect(() => {
    if (!user) { setOnboardingComplete(null); return; }
    const checkOnboarding = async () => {
      try {
        const res = await api.get('/onboarding/status');
        setOnboardingComplete(res.data.onboarding_complete);
      } catch {
        setOnboardingComplete(true); // Fallback: don't block
      }
    };
    checkOnboarding();
  }, [user]);

  const handleLogin = (userData) => {
    setUser(userData);
    refreshSub();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setOnboardingComplete(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--t-bg)' }}>
        <div className="w-10 h-10 border-2 rounded-full animate-spin" style={{ borderColor: '#0D9488', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  const P = ({ children }) => <ProtectedLayout user={user} onLogout={handleLogout}>{children}</ProtectedLayout>;

  // "Coming Soon" wrapper
  const CS = ({ title, desc }) => <P><ComingSoonPage title={title} description={desc} /></P>;

  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors theme={theme} />
      <UpgradeModal
        open={!!upgradeInfo}
        onClose={() => setUpgradeInfo(null)}
        feature={upgradeInfo?.message}
        requiredPlan={upgradeInfo?.required_plan || 'pro'}
      />
      <Routes>
        <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage onLogin={handleLogin} />} />
        <Route path="/register" element={user ? <Navigate to="/dashboard" replace /> : <RegisterPage onLogin={handleLogin} />} />
        {/* Public marketing pages - accessible regardless of auth */}
        <Route path="/features" element={user ? <Navigate to="/dashboard" replace /> : <FeaturesPage />} />
        <Route path="/how-it-works" element={user ? <Navigate to="/dashboard" replace /> : <HowItWorksPage />} />

        {/* Public routes - no auth required */}
        <Route path="/public/assessments/:shareId" element={<PublicSharedAssessmentPage />} />
        <Route path="/public/resume/:shareId" element={<PublicSharedResumePage />} />

        {user ? (
          <>
            {/* Onboarding check - force to onboarding if not complete */}
            <Route path="/onboarding" element={
              onboardingComplete
                ? <Navigate to="/dashboard" replace />
                : <OnboardingPage onComplete={() => setOnboardingComplete(true)} />
            } />

            {/* If onboarding not done, redirect all routes to onboarding */}
            {onboardingComplete === false ? (
              <Route path="*" element={<Navigate to="/onboarding" replace />} />
            ) : (
              <>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<P><DashboardPage /></P>} />
                <Route path="/resume" element={<P><ResumePage /></P>} />
                <Route path="/profile" element={<P><ProfilePage /></P>} />
                <Route path="/settings" element={<P><SettingsPage /></P>} />
                <Route path="/interview-prep" element={<P><InterviewPrepPage /></P>} />
                <Route path="/community" element={<P><CommunityPage /></P>} />
                <Route path="/achievements" element={<P><GamificationPage /></P>} />
                <Route path="/pricing" element={<P><SubscriptionPage /></P>} />
                <Route path="/subscription" element={<P><SubscriptionPage /></P>} />
                <Route path="/referral" element={<P><ReferralPage /></P>} />
                <Route path="/analytics" element={<P><AnalyticsPage /></P>} />
                <Route path="/payment-success" element={<P><PaymentSuccessPage /></P>} />

                {/* Career Intelligence */}
                <Route path="/career-assessment" element={<P><KaireraAssessmentPage /></P>} />
                <Route path="/career-batteries" element={<P><KaireraAssessmentBatteriesPage /></P>} />
                <Route path="/career-report" element={<P><KaireraReportPage /></P>} />
                <Route path="/career-tools" element={<P><KaireraToolsPage /></P>} />
                <Route path="/career-intel" element={<P><KaireraDashboardPage /></P>} />
                <Route path="/linkedin-optimizer" element={<P><LinkedInOptimizerPage /></P>} />
                <Route path="/company-intel" element={<P><CompanyIntelPage /></P>} />
                <Route path="/career-simulator" element={<P><CareerSimulatorPage /></P>} />

                {/* Coming Soon - v2.0 */}
                <Route path="/jobs" element={<CS title="Job Search" desc="Intelligent job matching based on your profile and Career Intelligence results. Complete your CI assessment to unlock personalized job recommendations." />} />
                <Route path="/jobs/:jobId" element={<CS title="Job Details" desc="Detailed job analysis coming in v2.0." />} />
                <Route path="/compare" element={<CS title="Job Compare" desc="Side-by-side job comparison coming in v2.0." />} />
                <Route path="/gap-analysis" element={<P><GapAnalysisPage /></P>} />
                <Route path="/roadmap" element={<CS title="Career Roadmap" desc="Personalized roadmap generated from your Career Intelligence results and goals." />} />
                <Route path="/future-skills" element={<CS title="Future Skills" desc="Smart skill recommendations based on your profile and market demand. Complete your profile and CI assessment to unlock." />} />
                <Route path="/strategy" element={<CS title="Career Strategy" desc="Strategic career planning powered by your CI results." />} />
                <Route path="/calendar" element={<CS title="Calendar" desc="Interview scheduling and milestone tracking coming in v2.0." />} />
                <Route path="/ai-history" element={<CS title="Interaction History" desc="Your complete interaction history coming in v2.0." />} />
                <Route path="/career-roadmap" element={<CS title="Career Roadmap" desc="Personalized career roadmap based on your goals and CI results." />} />
                <Route path="/assessments" element={<CS title="Legacy Assessments" desc="Replaced by Career Intelligence. Go to Career Intelligence to take your assessments." />} />
                <Route path="/insights" element={<CS title="Insights" desc="Career insights dashboard powered by your CI results." />} />

                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </>
            )}
          </>
        ) : (
          <>
            <Route path="/" element={<LandingPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <ThemeProvider>
      <SubscriptionProvider>
        <AppContent />
      </SubscriptionProvider>
    </ThemeProvider>
  );
}

export default App;
